import {
  type Dispatch,
  type InputHTMLAttributes,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Controller, type Path, useFormContext } from 'react-hook-form';
import { type FieldValues } from 'react-hook-form';

import { useHookFormMask } from 'use-mask-input';

import { CustomTooltipProps } from '../../../molecules/Tooltip/Tooltip';
import { get } from '../../../utils';
import { InputBackgroundFragment, InputTextVariant, RegularInputFragment } from './fragments';

export type TextInputProps = InputHTMLAttributes<HTMLInputElement> & {
  isComplete?: boolean;
  isLocked?: boolean;
  name: string;
  isFocused?: boolean;
  setIsFocused?: Dispatch<SetStateAction<boolean>>;
  isError?: boolean;
  className?: string;
  backgroundClassName?: string;
  inputClassName?: string;
  icon?: ReactNode;
  IconTag?: 'button' | 'div';
  iconPosition?: 'left' | 'right';
  onIconCtaClick?: (event: React.MouseEvent<HTMLElement>) => void;
  errorMessage?: ReactNode;
  register?: (name: string, mask?: string) => any;
  hasIconDivider?: boolean;
  variant?: InputTextVariant;
  mask?: string;
  maskPlaceholder?: string;
  helpTooltip?: ReactNode;
  helpToolTipPlacement?: CustomTooltipProps['placement'];
  suffix?: ReactNode;
  prefix?: ReactNode;
  backgroundStyle?: React.CSSProperties;
  maxLength?: number;
  showCharacterCount?: boolean;
  /** Marquee-scroll the overflowing value on hover (see useScrollText). */
  scrollOnHover?: boolean;
};

export function TextInput({
  isComplete = false,
  isLocked = false,
  className,
  inputClassName,
  backgroundClassName,
  iconPosition = 'left',
  icon,
  name,
  onIconCtaClick,
  type = 'text',
  isFocused = false,
  setIsFocused,
  isError = false,
  errorMessage,
  IconTag = 'div',
  register,
  disabled,
  hasIconDivider,
  variant,
  helpTooltip,
  helpToolTipPlacement,
  mask,
  maskPlaceholder,
  suffix,
  prefix,
  backgroundStyle,
  maxLength,
  showCharacterCount = false,
  scrollOnHover = false,
  ...otherProps
}: TextInputProps) {
  return (
    <InputBackgroundFragment
      isLocked={isLocked}
      isComplete={isComplete}
      className={className}
      backgroundClassName={backgroundClassName}
      icon={icon}
      IconTag={IconTag}
      iconPosition={iconPosition}
      onIconCtaClick={onIconCtaClick}
      isFocused={isFocused}
      isError={isError}
      errorMessage={errorMessage}
      disabled={disabled}
      hasIconDivider={hasIconDivider}
      variant={variant}
      helpTooltip={helpTooltip}
      helpToolTipPlacement={helpToolTipPlacement}
      suffix={suffix}
      prefix={prefix}
      backgroundStyle={backgroundStyle}
      name={name}
      maxLength={maxLength}
      showCharacterCount={showCharacterCount}
    >
      <RegularInputFragment
        isComplete={isComplete}
        isLocked={isLocked}
        onIconCtaClick={onIconCtaClick}
        inputClassName={inputClassName}
        iconPosition={iconPosition}
        icon={icon}
        name={name}
        type={type}
        setIsFocused={setIsFocused}
        isError={isError}
        variant={variant}
        register={register}
        disabled={disabled}
        hasIconDivider={hasIconDivider}
        mask={mask}
        maskPlaceholder={maskPlaceholder}
        maxLength={maxLength}
        scrollOnHover={scrollOnHover}
        {...otherProps}
      />
    </InputBackgroundFragment>
  );
}

export type _TextInputRhfProps<T> = {
  isLocked?: boolean;
  isComplete?: boolean;
  isError?: boolean;
  className?: string;
  inputClassName?: string;
  backgroundClassName?: string;
  name: Path<T>;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  onIconCtaClick?: (name: Path<T>) => void;
  hasIconDivider?: boolean;
  helpTooltip?: ReactNode;
  helpToolTipPlacement?: CustomTooltipProps['placement'];
  mask?: string;
  maskPlaceholder?: string;
  suffix?: ReactNode;
  prefix?: ReactNode;
  skipUnregisterOnUnmount?: boolean;
  errorMessage?: ReactNode;
  maxLength?: number;
  showCharacterCount?: boolean;
};

export type TextInputRhfProps<T extends FieldValues> = InputHTMLAttributes<HTMLInputElement> &
  _TextInputRhfProps<T>;

export function TextInputRhf<T extends FieldValues>({
  onIconCtaClick,
  name,
  helpTooltip,
  helpToolTipPlacement,
  mask,
  maskPlaceholder,
  suffix,
  prefix,
  isError,
  errorMessage,
  skipUnregisterOnUnmount = false,
  ...otherProps
}: TextInputRhfProps<T>) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const {
    register,
    unregister,
    getFieldState,
    control,
    formState: { errors },
  } = useFormContext<T>();

  const registerWithMask = useHookFormMask(register);

  const IconTag = useMemo(() => (onIconCtaClick ? 'button' : 'div'), [onIconCtaClick]);

  const handleIconCtaClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      onIconCtaClick?.(name);
    },
    [name, onIconCtaClick],
  );

  useEffect(() => {
    return () => {
      if (skipUnregisterOnUnmount) {
        return;
      }
      const { isDirty } = getFieldState(name as Path<T>);
      unregister(name as Path<T>, { keepDirty: true, keepDefaultValue: true, keepValue: isDirty });
    };
  }, [name, unregister, getFieldState]);

  const registerToUse = mask ? registerWithMask : register;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, name: alias } }) => {
        const fieldError = get(errors, alias);

        return (
          <TextInput
            name={alias}
            register={registerToUse as any}
            mask={mask}
            maskPlaceholder={maskPlaceholder}
            IconTag={IconTag}
            onIconCtaClick={onIconCtaClick && handleIconCtaClick}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            isError={!!fieldError || isError}
            errorMessage={(fieldError?.message as string) || errorMessage}
            helpTooltip={helpTooltip}
            helpToolTipPlacement={helpToolTipPlacement}
            suffix={suffix}
            prefix={prefix}
            value={value}
            {...otherProps}
          />
        );
      }}
    />
  );
}

export default TextInput;
